import { EntityType } from '@/enums/entityType.enum';
import { PublicSettings } from '@/enums/publicSettings.enum';
import { DocumentIndex } from '@/interfaces/documentIndex';
import { Procedure } from '@/interfaces/procedure';
import { ProcedureResource, procedureResource } from '@/resources/procedure.resource';
import store from '../store';
import { EntityModule } from './entity.module';
import attachment from './procedure/attachment.module';

/**
 * Moduł procedur
 *
 * @export
 * @class ProcedureModule
 * @extends {EntityModule<Procedure, ProcedureResource>}
 */
export class ProcedureModule extends EntityModule<Procedure, ProcedureResource> {
  public modules = { attachment };
  constructor() {
    super(procedureResource);

    this.state = {
      ...this.state,
      paymentList: undefined,
      paymentListSchools: undefined,
      paymentListSplit: undefined,
      plan: undefined,
      templatesMap: {},
      tabs: undefined
    } as any;

    this.mutations = {
      ...this.mutations,

      /**
       * Zapisuje listę płatności
       *
       * @param {*} state
       * @param {DocumentIndex[]} paymentList
       */
      savePaymentList(state: any, paymentList: DocumentIndex[]): void {
        state.paymentList = paymentList;
      },

      /**
       * Zapisuje szkoły dla których możliwe jest wygenerowanie listy płatności
       *
       * @param {*} state
       * @param {string[]} schools
       */
      savePaymentListSchools(state: any, schools: string[]): void {
        state.paymentListSchools = schools;
      },

      /**
       * Zapisuje rozkład źródeł wypłat
       *
       * @param {*} state
       * @param {{ grant: number; contribution: number }} split
       */
      savePaymentListSplit(state: any, split: { grant: number; contribution: number }): void {
        state.paymentListSplit = split;
      },

      /**
       * Zapisuje plan
       *
       * @param {*} state
       * @param {string[][]} plan
       */
      savePlan(state: any, plan: string[][]): void {
        state.plan = plan;
      },

      /**
       * Zapisuje wartość do mapy ID wzoru - ID procedury
       *
       * @param {*} state
       * @param {{procedureId: number, templateId: number}} data
       */
      saveTemplateProcedure(state: any, data: { procedureId: number; templateId: number }): void {
        state.templatesMap[data.templateId] = data.procedureId;
      },

      /**
       * Zapisuje zakładki
       *
       * @param {*} state
       * @param {any[]} tabs
       */
      saveTabs(state: any, tabs: any[]): void {
        state.tabs = tabs;
      }
    };

    this.actions = {
      ...this.actions,
      /**
       * Pobranie identyfikatora procedury na podstawie wzoru procedury
       *
       * @param {*} context
       * @param {number} templateId
       * @returns {Promise<number>}
       */
      getIdByTemplate: (context: any, templateId: number): Promise<number> =>
        this.resource.idByTemplateId(templateId).then(procedureId => {
          context.commit('saveTemplateProcedure', { procedureId, templateId });
          return procedureId;
        }),

      /**
       * Ustawia status dokumentu
       */
      setStatus: (context: any, data: any): Promise<number> => this.resource.setStatus(data.id, data.statusId),

      /**
       * Pobiera listę płatności
       *
       * @param {*} context
       * @returns {Promise<string>}
       */
      paymentList: (context: any, data: any): Promise<DocumentIndex[]> =>
        this.resource
          .paymentList({ ...data, id: undefined, procedureId: data.id || context.state.id })
          .then(paymentList => {
            context.commit(
              'savePaymentList',
              paymentList.map(item => ({ ...item, checked: true }))
            );
            return paymentList;
          }),

      /**
       * Pobiera szkoły dla których możliwe jest wygenerowanie listy płatności
       *
       * @param {*} context
       * @param {*} data
       * @returns {Promise<string[]>}
       */
      paymentListSchools: (context: any, data: any): Promise<string[]> =>
        this.resource
          .paymentListSchools({ ...data, id: undefined, procedureId: data.id || context.state.id })
          .then(schools => {
            context.commit('savePaymentListSchools', schools);
            return schools;
          }),

      /**
       * Pobiera rozkład źródeł wypłat
       *
       * @param {*} context
       * @returns {Promise<string>}
       */
      paymentListSplit: (context: any, data: any): Promise<{ grant: number; contribution: number }> =>
        this.resource
          .paymentListSplit({ ...data, id: undefined, procedureId: data.id || context.state.id })
          .then(split => {
            context.commit('savePaymentListSplit', split);
            return split;
          }),

      /**
       * Przesyła listę płatności do KSAT
       *
       * @param {*} context
       * @param {*} {id, date}
       * @returns {Promise<number>}
       */
      paymentListSendToKsat: (context: any, data: any): Promise<number> =>
        this.resource.paymentListSendToKsat({ ...data, id: undefined, procedureId: data.id || context.state.id }),

      /**
       * Przesyła listę płatności do MDOK
       *
       * @param {*} context
       * @param {*} {id, date}
       * @returns {Promise<number>}
       */
      paymentListSendToMdok: (context: any, data: any): Promise<number> =>
        this.resource.paymentListSendToMdok({ ...data, id: undefined, procedureId: data.id || context.state.id }),

      /**
       * Pobiera plan roczny
       *
       * @param {*} context
       * @param {*} data
       * @returns {Promise<string[][]>}
       */
      plan: (context: any, data): Promise<string[][]> =>
        this.resource.plan(data).then(plan => {
          context.commit('savePlan', plan);
          return plan;
        }),

      /**
       * Tworzenie procedury na podstawie już istniejącej
       *
       * @param {*} context
       * @param {*} data
       * @returns {Promise<number>}
       */
      copy: (context: any, data: any): Promise<number> => this.resource.copy(data),

      /**
       * Pobiera zakładki procedury
       *
       * @param {*} context
       * @returns {Promise<any>}
       */
      getTabs: (context: any, { entityType, entityId }): Promise<any> =>
        store
          .dispatch('setting/findPublic', {
            code: PublicSettings.PROCEDURE_TABS,
            entityType,
            entityId
          })
          .then(tabsSetting => {
            const tabs: any[] = JSON.parse(tabsSetting.value);
            return tabs
              .map(tab => ({
                id: tab.order,
                title: tab.name,
                children: tab.tabs
                  .filter((child: any) => !child.secretaryOnly || store.state.auth.nodes?.length)
                  .map((child: any) => {
                    const childTab: any = {
                      entityId: child.entityId,
                      entityType: child.entityType,
                      permissions: child.permissions || ['DOCUMENT.LIST'],
                      joinType: child.joinType,
                      route: {
                        name: 'documentList',
                        query: {
                          tabId: tab.order,
                          statusId: child.statusId,
                          previousStatuses: child.previousStatuses,
                          notPreviousStatus: child.notPreviousStatus
                        }
                      },
                      title: child.name
                    };
                    if (tab.checkStatusPermissions) {
                      childTab.entityType = EntityType.STATUS;
                      childTab.entityId = child.statusId;
                    }
                    return childTab;
                  })
              }))
              .filter(tab => tab.children?.length);
          })
          .then(tabs => {
            context.commit('saveTabs', tabs);
            return tabs;
          })
    };
  }
}

export default new ProcedureModule();
